import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, MapPin, Calendar, Download, Search, Check, 
  Clock, ChevronDown, X, RefreshCw
} from 'lucide-react';
import AdminLayout, { usePermissions } from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

/** YYYY-MM-DD en fuseau local (évite le décalage UTC de toISOString). */
function toLocalYMD(dateObj) {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeScheduleTime(t) {
  if (t == null || typeof t !== 'string') return '';
  const trimmed = t.trim();
  const parts = trimmed.split(':');
  if (parts.length < 2) return trimmed;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return trimmed;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function findPointageForOpening(dayPointages, opening) {
  if (!dayPointages?.length) return undefined;
  const wantT = normalizeScheduleTime(opening.time);
  const wantType = opening.type === 'fermeture' ? 'fermeture' : 'ouverture';
  const byTimeAndType = dayPointages.find(
    (p) =>
      normalizeScheduleTime(p.expected_time) === wantT &&
      (p.scan_type === wantType || p.scan_type == null || p.scan_type === '')
  );
  if (byTimeAndType) return byTimeAndType;
  return dayPointages.find((p) => normalizeScheduleTime(p.expected_time) === wantT);
}

/** Statuts API (scan) → libellés compte rendu (PDF / CSV / Excel). */
function pointageToCompteRenduStatus(p) {
  if (!p) return 'Non effectué';
  const s = String(p.status || '').toLowerCase();
  if (s === 'valid' || s === 'en_avance' || s === 'a_lheure') return 'Validé';
  if (s === 'en_retard' || s === 'hors_marge') return 'Hors délai';
  if (s === 'absent') return 'Absent';
  return 'Hors délai';
}

export default function AdminCompteRendu() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  
  // Check permission - redirect if not allowed
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/admins/my-permissions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!response.data?.permissions?.can_create_reports) {
            toast.error('Accès non autorisé');
            navigate('/admin/dashboard');
          }
        }
      } catch (error) {
        // If error, let the normal auth flow handle it
      }
    };
    checkPermission();
  }, [navigate]);
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  
  // Period selection
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Type filter
  const [includeOuverture, setIncludeOuverture] = useState(true);
  const [includeFermeture, setIncludeFermeture] = useState(true);
  
  // Delay/Advance options
  const [showDelayMinutes, setShowDelayMinutes] = useState(false);
  const [showAdvanceMinutes, setShowAdvanceMinutes] = useState(false);
  
  // Schedule selection
  const [siteSchedule, setSiteSchedule] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  
  // Custom message
  const [customMessage, setCustomMessage] = useState('');
  
  // Export menu
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  
  // Loading
  const [loading, setLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Dropdown position - calculate before opening for smooth appearance
  const [dropdownPosition, setDropdownPosition] = useState(null);
  
  // Calculate dropdown position before it opens
  const openSiteDropdown = () => {
    const trigger = document.getElementById('site-dropdown-trigger');
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
    setSiteDropdownOpen(true);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    
    fetchSites();
    
    // Set default dates (current week)
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    setStartDate(monday.toISOString().split('T')[0]);
    setEndDate(sunday.toISOString().split('T')[0]);
  }, [navigate]);

  useEffect(() => {
    if (searchQuery) {
      setFilteredSites(sites.filter(site => 
        site.nom.toLowerCase().includes(searchQuery.toLowerCase())
      ));
    } else {
      setFilteredSites(sites);
    }
  }, [searchQuery, sites]);

  useEffect(() => {
    if (selectedSiteId) {
      fetchSiteSchedule(selectedSiteId);
    }
  }, [selectedSiteId]);

  const fetchSites = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use secteurs endpoint which fetches from Prysma
      const response = await axios.get(`${API}/secteurs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data);
      setFilteredSites(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des sites');
    }
  };

  const fetchSiteSchedule = async (siteId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch weekly planning from Prysma (dynamic schedules)
      const today = new Date();
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(today);
      monday.setDate(today.getDate() + diff);
      const weekStartStr = monday.toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/planning/weekly/${siteId}?week_start=${weekStartStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transform weekly planning to flat openings format
      const flatOpenings = [];
      const dayNameToIndex = {
        'monday': 0, 'tuesday': 1, 'wednesday': 2, 'thursday': 3,
        'friday': 4, 'saturday': 5, 'sunday': 6
      };
      
      if (response.data.days) {
        response.data.days.forEach(dayData => {
          const dayName = dayData.day_name;
          const dayIndex = dayNameToIndex[dayName] ?? 0;
          const schedule = dayData.schedule;
          
          if (schedule && !schedule.closed && schedule.openings) {
            schedule.openings.forEach(opening => {
              flatOpenings.push({
                day: dayIndex,
                time: opening.time,
                type: opening.type || 'ouverture',
                margin_before: opening.margin_before || 30,
                margin_after: opening.margin_after || 5,
                description: opening.description || ''
              });
            });
          }
        });
      }
      
      setSiteSchedule(flatOpenings);
      setSelectedSchedules(flatOpenings.map((_, idx) => idx)); // Select all by default
    } catch (error) {
      console.error('Error fetching schedule:', error);
      setSiteSchedule([]);
      setSelectedSchedules([]);
    }
  };

  const toggleSchedule = (index) => {
    if (selectedSchedules.includes(index)) {
      setSelectedSchedules(selectedSchedules.filter(i => i !== index));
    } else {
      setSelectedSchedules([...selectedSchedules, index]);
    }
  };

  const selectAllSchedules = () => {
    setSelectedSchedules(siteSchedule.map((_, idx) => idx));
  };

  const deselectAllSchedules = () => {
    setSelectedSchedules([]);
  };

  const fetchReportData = async () => {
    const token = localStorage.getItem('token');
    const site = sites.find(s => s.id === selectedSiteId);
    
    // Calculate all dates in the period
    const start = new Date(startDate);
    const end = new Date(endDate);
    const allData = [];
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday=0
      const dateStr = toLocalYMD(d);
      
      // Get openings for this day
      const dayOpenings = siteSchedule.filter((opening, idx) => {
        if (!selectedSchedules.includes(idx)) return false;
        if (opening.day !== dayIndex) return false;
        if (!includeOuverture && opening.type !== 'fermeture') return false;
        if (!includeFermeture && opening.type === 'fermeture') return false;
        return true;
      });
      
      for (const opening of dayOpenings) {
        // Try to find pointage for this opening
        try {
          const weekStart = new Date(d);
          const diff = weekStart.getDay() === 0 ? -6 : 1 - weekStart.getDay();
          weekStart.setDate(weekStart.getDate() + diff);
          
          const response = await axios.get(`${API}/planning/weekly/${selectedSiteId}`, {
            params: { week_start: toLocalYMD(weekStart) },
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const dayData = response.data.days?.find((day) => day.date === dateStr);
          const pointage = findPointageForOpening(dayData?.pointages, opening);
          
          // Calculate delay/advance in minutes
          let delayMinutes = null;
          if (pointage) {
            const scheduledParts = opening.time.split(':');
            const scheduledDate = new Date(d);
            scheduledDate.setHours(parseInt(scheduledParts[0]), parseInt(scheduledParts[1]), 0, 0);
            
            const scanDate = new Date(pointage.timestamp);
            delayMinutes = Math.round((scanDate - scheduledDate) / 60000); // positive = late, negative = early
          }
          
          allData.push({
            date: new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
            dateRaw: new Date(d),
            type: opening.type === 'fermeture' ? 'Fermeture' : 'Ouverture',
            scheduledTime: opening.time,
            description: opening.description || '',
            employee: pointage ? `${pointage.prenom} ${pointage.nom}` : '',
            scanTime: pointage ? new Date(pointage.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '',
            status: pointageToCompteRenduStatus(pointage),
            hasPointage: !!pointage,
            delayMinutes: delayMinutes
          });
        } catch (error) {
          allData.push({
            date: new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
            dateRaw: new Date(d),
            type: opening.type === 'fermeture' ? 'Fermeture' : 'Ouverture',
            scheduledTime: opening.time,
            description: opening.description || '',
            employee: '',
            scanTime: '',
            status: 'Non effectué',
            hasPointage: false,
            delayMinutes: null
          });
        }
      }
    }
    
    return allData.sort((a, b) => a.dateRaw - b.dateRaw);
  };

  // Helper function to load image as base64
  const loadImageAsBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const generatePDF = async () => {
    setGeneratingReport(true);
    
    try {
      const data = await fetchReportData();
      const site = sites.find(s => s.id === selectedSiteId);
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Colors
      const primaryColor = [91, 164, 207]; // #5BA4CF
      const darkText = [30, 41, 59]; // slate-800
      const lightGray = [248, 250, 252]; // slate-50
      const mediumGray = [100, 116, 139]; // slate-500
      
      // ===== HEADER SECTION =====
      // White header with subtle bottom border
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      // Add logo
      try {
        const logoBase64 = await loadImageAsBase64('/logo-myopenclose.png');
        doc.addImage(logoBase64, 'PNG', 12, 5, 50, 35);
      } catch (e) {
        // Fallback text if logo fails
        doc.setTextColor(...primaryColor);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('My Open Close', 15, 28);
      }
      
      // Title on the right
      doc.setTextColor(...darkText);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('COMPTE RENDU', pageWidth - 15, 20, { align: 'right' });
      
      // Subtitle - Site name
      doc.setTextColor(...primaryColor);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(site?.nom || 'Site', pageWidth - 15, 30, { align: 'right' });
      
      // Header bottom line
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.8);
      doc.line(15, 45, pageWidth - 15, 45);
      
      // ===== INFO CARDS SECTION =====
      let y = 55;
      
      // Info box background
      doc.setFillColor(...lightGray);
      doc.roundedRect(15, y, pageWidth - 30, 28, 3, 3, 'F');
      
      // Period
      doc.setTextColor(...mediumGray);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('PÉRIODE', 22, y + 8);
      doc.setTextColor(...darkText);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      const periodText = `${new Date(startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} - ${new Date(endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      doc.text(periodText, 22, y + 18);
      
      // Type
      doc.setTextColor(...mediumGray);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('TYPE', pageWidth - 70, y + 8);
      doc.setTextColor(...darkText);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      const typeText = includeOuverture && includeFermeture ? 'Ouvertures & Fermetures' : 
                       includeOuverture ? 'Ouvertures' : 'Fermetures';
      doc.text(typeText, pageWidth - 70, y + 18);
      
      y += 35;
      
      // Custom message
      if (customMessage) {
        doc.setFillColor(255, 251, 235); // amber-50
        doc.setDrawColor(251, 191, 36); // amber-400
        doc.setLineWidth(0.3);
        doc.roundedRect(15, y, pageWidth - 30, 18, 2, 2, 'FD');
        doc.setTextColor(146, 64, 14); // amber-800
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.text(`« ${customMessage} »`, 22, y + 11, { maxWidth: pageWidth - 44 });
        y += 25;
      }
      
      // ===== TABLE SECTION =====
      y += 5;
      
      // Table header with rounded corners effect
      const tableX = 15;
      const tableWidth = pageWidth - 30;
      
      // Header background
      doc.setFillColor(...primaryColor);
      doc.roundedRect(tableX, y, tableWidth, 12, 2, 2, 'F');
      
      // Header text
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      
      // Dynamic columns based on options
      const hasDelayCol = showDelayMinutes || showAdvanceMinutes;
      const colWidths = hasDelayCol ? [45, 35, 35, 30, 35] : [55, 45, 40, 40];
      
      let colX = tableX + 6;
      doc.text('Date', colX, y + 8);
      colX += colWidths[0];
      doc.text('Type', colX, y + 8);
      colX += colWidths[1];
      doc.text('Heure prevue', colX, y + 8);
      colX += colWidths[2];
      doc.text('Statut', colX, y + 8);
      if (hasDelayCol) {
        colX += colWidths[3];
        doc.text('Ecart', colX, y + 8);
      }
      
      y += 14;
      
      // Table rows
      const rowHeight = 12;
      let rowCount = 0;
      let totalDelayMinutes = 0;
      let totalAdvanceMinutes = 0;
      
      const drawTableHeader = (yPos) => {
        doc.setFillColor(...primaryColor);
        doc.roundedRect(tableX, yPos, tableWidth, 12, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        let cx = tableX + 6;
        doc.text('Date', cx, yPos + 8); cx += colWidths[0];
        doc.text('Type', cx, yPos + 8); cx += colWidths[1];
        doc.text('Heure prevue', cx, yPos + 8); cx += colWidths[2];
        doc.text('Statut', cx, yPos + 8);
        if (hasDelayCol) {
          cx += colWidths[3];
          doc.text('Ecart', cx, yPos + 8);
        }
        return yPos + 14;
      };
      
      for (const row of data) {
        if (y > pageHeight - 40) {
          doc.addPage();
          y = 20;
          y = drawTableHeader(y);
        }
        
        // Alternate row colors
        if (rowCount % 2 === 0) {
          doc.setFillColor(...lightGray);
          doc.rect(tableX, y - 2, tableWidth, rowHeight, 'F');
        }
        
        // Row border
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.2);
        doc.line(tableX, y + rowHeight - 2, tableX + tableWidth, y + rowHeight - 2);
        
        doc.setTextColor(...darkText);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        colX = tableX + 6;
        
        // Date - formatted nicely
        const dateObj = row.dateRaw;
        const dayName = dateObj.toLocaleDateString('fr-FR', { weekday: 'short' });
        const dayNum = dateObj.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        doc.setFont('helvetica', 'bold');
        doc.text(`${dayName} ${dayNum}`, colX, y + 6);
        colX += colWidths[0];
        
        // Type - Full text without special characters
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...darkText);
        doc.text(row.type, colX, y + 6);
        colX += colWidths[1];
        
        // Scheduled time
        doc.setFont('helvetica', 'bold');
        doc.text(row.scheduledTime, colX, y + 6);
        colX += colWidths[2];
        
        // Status with colored badge
        let statusColor, statusBg, statusText;
        if (row.status === 'Validé') {
          statusColor = [22, 163, 74];
          statusBg = [220, 252, 231];
          statusText = 'Present';
        } else if (row.status === 'Hors délai') {
          statusColor = [220, 38, 38];
          statusBg = [254, 226, 226];
          statusText = 'Retard';
        } else if (row.status === 'Absent') {
          statusColor = [107, 114, 128];
          statusBg = [243, 244, 246];
          statusText = 'Absent';
        } else {
          statusColor = [107, 114, 128];
          statusBg = [243, 244, 246];
          statusText = 'Absent';
        }
        
        // Status badge - wider and properly sized
        doc.setFillColor(...statusBg);
        doc.roundedRect(colX, y - 1, 28, 9, 2, 2, 'F');
        doc.setTextColor(...statusColor);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(statusText, colX + 14, y + 5, { align: 'center' });
        
        // Delay/Advance column
        if (hasDelayCol) {
          colX += colWidths[3];
          
          if (row.delayMinutes !== null && row.hasPointage) {
            let ecartText = '';
            let ecartColor = mediumGray;
            
            if (row.delayMinutes > 0 && showDelayMinutes) {
              ecartText = `+${row.delayMinutes} min`;
              ecartColor = [220, 38, 38]; // red
              totalDelayMinutes += row.delayMinutes;
            } else if (row.delayMinutes < 0 && showAdvanceMinutes) {
              ecartText = `${row.delayMinutes} min`;
              ecartColor = [22, 163, 74]; // green
              totalAdvanceMinutes += Math.abs(row.delayMinutes);
            } else if (row.delayMinutes === 0) {
              ecartText = '0 min';
              ecartColor = mediumGray;
            }
            
            if (ecartText) {
              doc.setTextColor(...ecartColor);
              doc.setFontSize(9);
              doc.setFont('helvetica', 'normal');
              doc.text(ecartText, colX, y + 6);
            }
          }
        }
        
        y += rowHeight;
        rowCount++;
      }
      
      // ===== SUMMARY SECTION =====
      y += 15;
      
      if (y > pageHeight - 50) {
        doc.addPage();
        y = 20;
      }
      
      const validated = data.filter(d => d.status === 'Validé').length;
      const notDone = data.filter(d => d.status === 'Non effectué').length;
      const outOfTime = data.filter(d => d.status === 'Hors délai' || d.status === 'Absent').length;
      const validationRate = data.length > 0 ? Math.round((validated / data.length) * 100) : 0;
      
      // Summary title
      doc.setTextColor(...darkText);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Synthèse', 15, y);
      y += 10;
      
      // Stats cards
      const cardWidth = (pageWidth - 50) / 4;
      const cardHeight = 25;
      const cardY = y;
      
      // Card 1 - Total
      doc.setFillColor(...lightGray);
      doc.roundedRect(15, cardY, cardWidth, cardHeight, 3, 3, 'F');
      doc.setTextColor(...mediumGray);
      doc.setFontSize(8);
      doc.text('TOTAL', 15 + cardWidth/2, cardY + 8, { align: 'center' });
      doc.setTextColor(...darkText);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(String(data.length), 15 + cardWidth/2, cardY + 20, { align: 'center' });
      
      // Card 2 - Validés
      doc.setFillColor(220, 252, 231);
      doc.roundedRect(25 + cardWidth, cardY, cardWidth, cardHeight, 3, 3, 'F');
      doc.setTextColor(22, 163, 74);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('VALIDÉS', 25 + cardWidth + cardWidth/2, cardY + 8, { align: 'center' });
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(String(validated), 25 + cardWidth + cardWidth/2, cardY + 20, { align: 'center' });
      
      // Card 3 - Absents
      doc.setFillColor(243, 244, 246);
      doc.roundedRect(35 + cardWidth * 2, cardY, cardWidth, cardHeight, 3, 3, 'F');
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('ABSENTS', 35 + cardWidth * 2 + cardWidth/2, cardY + 8, { align: 'center' });
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(String(notDone), 35 + cardWidth * 2 + cardWidth/2, cardY + 20, { align: 'center' });
      
      // Card 4 - Hors délai
      doc.setFillColor(254, 226, 226);
      doc.roundedRect(45 + cardWidth * 3, cardY, cardWidth, cardHeight, 3, 3, 'F');
      doc.setTextColor(220, 38, 38);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('HORS DÉLAI', 45 + cardWidth * 3 + cardWidth/2, cardY + 8, { align: 'center' });
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(String(outOfTime), 45 + cardWidth * 3 + cardWidth/2, cardY + 20, { align: 'center' });
      
      y = cardY + cardHeight + 10;
      
      // Delay/Advance totals (if enabled)
      if (hasDelayCol && (totalDelayMinutes > 0 || totalAdvanceMinutes > 0)) {
        const formatDuration = (minutes) => {
          const h = Math.floor(minutes / 60);
          const m = minutes % 60;
          return h > 0 ? `${h}h ${m}min` : `${m} min`;
        };
        
        // Delay/Advance summary box
        doc.setFillColor(255, 251, 235); // amber-50
        doc.setDrawColor(251, 191, 36); // amber-400
        doc.setLineWidth(0.3);
        doc.roundedRect(15, y, pageWidth - 30, 22, 3, 3, 'FD');
        
        let summaryX = 25;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(146, 64, 14); // amber-800
        doc.text('Bilan des écarts :', summaryX, y + 14);
        
        summaryX += 60;
        
        if (showDelayMinutes && totalDelayMinutes > 0) {
          doc.setTextColor(220, 38, 38); // red
          doc.text(`Total retards: ${formatDuration(totalDelayMinutes)}`, summaryX, y + 14);
          summaryX += 60;
        }
        
        if (showAdvanceMinutes && totalAdvanceMinutes > 0) {
          doc.setTextColor(22, 163, 74); // green
          doc.text(`Total avances: ${formatDuration(totalAdvanceMinutes)}`, summaryX, y + 14);
        }
        
        y += 30;
      }
      
      // Validation rate bar
      doc.setFillColor(226, 232, 240);
      doc.roundedRect(15, y, pageWidth - 30, 8, 2, 2, 'F');
      if (validationRate > 0) {
        doc.setFillColor(22, 163, 74);
        doc.roundedRect(15, y, (pageWidth - 30) * validationRate / 100, 8, 2, 2, 'F');
      }
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      if (validationRate >= 20) {
        doc.text(`Taux de validation : ${validationRate}%`, 20, y + 6);
      }
      
      // ===== FOOTER =====
      const footerY = pageHeight - 15;
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(15, footerY - 5, pageWidth - 15, footerY - 5);
      
      doc.setTextColor(...mediumGray);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`, 15, footerY);
      doc.text('My Open Close - Service de gestion', pageWidth - 15, footerY, { align: 'right' });
      
      const fileName = `compte_rendu_${site?.nom.replace(/[^a-z0-9]/gi, '_')}_${startDate}_${endDate}.pdf`;
      doc.save(fileName);
      
      toast.success('PDF généré avec succès');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGeneratingReport(false);
    }
  };

  const generateCSV = async () => {
    setGeneratingReport(true);
    
    try {
      const data = await fetchReportData();
      const site = sites.find(s => s.id === selectedSiteId);
      
      // Dynamic headers based on options
      const hasDelayCol = showDelayMinutes || showAdvanceMinutes;
      let csvContent = hasDelayCol 
        ? 'Date;Type;Heure Prévue;Description;Employé;Heure Scan;Statut;Écart (min)\n'
        : 'Date;Type;Heure Prévue;Description;Employé;Heure Scan;Statut\n';
      
      let totalDelayMinutes = 0;
      let totalAdvanceMinutes = 0;
      
      for (const row of data) {
        let ecartValue = '';
        if (hasDelayCol && row.delayMinutes !== null && row.hasPointage) {
          if (row.delayMinutes > 0 && showDelayMinutes) {
            ecartValue = `+${row.delayMinutes}`;
            totalDelayMinutes += row.delayMinutes;
          } else if (row.delayMinutes < 0 && showAdvanceMinutes) {
            ecartValue = `${row.delayMinutes}`;
            totalAdvanceMinutes += Math.abs(row.delayMinutes);
          } else if (row.delayMinutes === 0) {
            ecartValue = '0';
          }
        }
        
        csvContent += hasDelayCol
          ? `"${row.date}";"${row.type}";"${row.scheduledTime}";"${row.description}";"${row.employee}";"${row.scanTime}";"${row.status}";"${ecartValue}"\n`
          : `"${row.date}";"${row.type}";"${row.scheduledTime}";"${row.description}";"${row.employee}";"${row.scanTime}";"${row.status}"\n`;
      }
      
      // Add totals if delay/advance options are enabled
      if (hasDelayCol) {
        csvContent += '\n';
        if (showDelayMinutes && totalDelayMinutes > 0) {
          csvContent += `"Total retards";;;;;"";"";"${totalDelayMinutes} min"\n`;
        }
        if (showAdvanceMinutes && totalAdvanceMinutes > 0) {
          csvContent += `"Total avances";;;;;"";"";"${totalAdvanceMinutes} min"\n`;
        }
      }
      
      if (customMessage) {
        csvContent += `\n"Message: ${customMessage}"\n`;
      }
      
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileName = `compte_rendu_${site?.nom.replace(/[^a-z0-9]/gi, '_')}_${startDate}_${endDate}.csv`;
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      
      toast.success('CSV généré avec succès');
    } catch (error) {
      toast.error('Erreur lors de la génération du CSV');
    } finally {
      setGeneratingReport(false);
    }
  };

  const generateExcel = async () => {
    setGeneratingReport(true);
    
    try {
      const data = await fetchReportData();
      const site = sites.find(s => s.id === selectedSiteId);
      
      const worksheetData = [
        ['COMPTE RENDU - MY OPEN CLOSE'],
        [''],
        ['Site:', site?.nom || ''],
        ['Période:', `Du ${new Date(startDate).toLocaleDateString('fr-FR')} au ${new Date(endDate).toLocaleDateString('fr-FR')}`],
        [''],
      ];
      
      if (customMessage) {
        worksheetData.push(['Message:', customMessage]);
        worksheetData.push(['']);
      }
      
      worksheetData.push(['Date', 'Type', 'Heure Prévue', 'Description', 'Employé', 'Heure Scan', 'Statut']);
      
      for (const row of data) {
        worksheetData.push([
          row.date,
          row.type,
          row.scheduledTime,
          row.description,
          row.employee,
          row.scanTime,
          row.status
        ]);
      }
      
      worksheetData.push(['']);
      const validated = data.filter(d => d.status === 'Validé').length;
      const notDone = data.filter(d => d.status === 'Non effectué').length;
      const outOfTime = data.filter(d => d.status === 'Hors délai' || d.status === 'Absent').length;
      worksheetData.push(['RÉSUMÉ']);
      worksheetData.push(['Total', data.length, 'Validés', validated, 'Non effectués', notDone, 'Hors délai', outOfTime]);
      worksheetData.push(['']);
      worksheetData.push([`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`]);
      
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet['!cols'] = [
        { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 20 }, { wch: 12 }, { wch: 15 }
      ];
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Compte Rendu');
      
      const fileName = `compte_rendu_${site?.nom.replace(/[^a-z0-9]/gi, '_')}_${startDate}_${endDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast.success('Excel généré avec succès');
    } catch (error) {
      toast.error('Erreur lors de la génération Excel');
    } finally {
      setGeneratingReport(false);
    }
  };

  const selectedSite = sites.find(s => s.id === selectedSiteId);
  const isDark = document.documentElement.classList.contains('dark');
  
  const filteredSchedule = siteSchedule.filter(opening => {
    if (!includeOuverture && opening.type !== 'fermeture') return false;
    if (!includeFermeture && opening.type === 'fermeture') return false;
    return true;
  });

  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Compte Rendu
          </h1>
          <p className="text-gray-600 dark:text-zinc-400 mt-2">
            Générez des rapports d'ouvertures et fermetures pour vos clients
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Site Selection */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#5BA4CF]" />
                Sélection du site
              </h3>
              
              <div className="relative">
                <button
                  id="site-dropdown-trigger"
                  onClick={() => siteDropdownOpen ? setSiteDropdownOpen(false) : openSiteDropdown()}
                  className="w-full h-12 px-4 flex items-center justify-between rounded-lg border text-left"
                  style={{
                    background: isDark ? '#374151' : '#f8f9fa',
                    borderColor: isDark ? '#4b5563' : '#e5e7eb'
                  }}
                >
                  <span className={selectedSite ? 'text-gray-900 dark:text-white' : 'text-gray-500'}>
                    {selectedSite ? (
                      <span className="flex items-center">
                        <MapPin className="h-4 w-4 mr-2 text-[#5BA4CF]" />
                        {selectedSite.nom}
                      </span>
                    ) : 'Sélectionnez un site'}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${siteDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
            
            {/* Site Dropdown Portal - Rendered outside normal flow */}
            {siteDropdownOpen && dropdownPosition && (
              <>
                <div 
                  className="fixed inset-0" 
                  style={{ zIndex: 9998 }}
                  onClick={() => setSiteDropdownOpen(false)} 
                />
                <div 
                  className="fixed rounded-lg shadow-2xl border animate-in fade-in slide-in-from-top-2 duration-150"
                  style={{
                    background: isDark ? '#1f2937' : '#ffffff',
                    borderColor: isDark ? '#374151' : '#e5e7eb',
                    zIndex: 9999,
                    width: dropdownPosition.width || 400,
                    left: dropdownPosition.left,
                    top: dropdownPosition.top,
                    maxHeight: '300px'
                  }}
                >
                  <div className="p-2 border-b" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Rechercher..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full h-10 pl-9 pr-3 rounded-lg border text-sm text-gray-900 dark:text-white"
                        style={{
                          background: isDark ? '#374151' : '#f8f9fa',
                          borderColor: isDark ? '#4b5563' : '#e5e7eb'
                        }}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto" style={{ maxHeight: '240px' }}>
                    {filteredSites.map(site => (
                      <button
                        key={site.id}
                        onClick={() => {
                          setSelectedSiteId(site.id);
                          setSiteDropdownOpen(false);
                          setSearchQuery('');
                        }}
                        className={`w-full px-4 py-2.5 flex items-center text-left transition-colors ${
                          selectedSiteId === site.id ? 'bg-[#5BA4CF]/20 text-[#5BA4CF]' : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-zinc-800'
                        }`}
                      >
                        <MapPin className="h-4 w-4 mr-2 text-[#5BA4CF]" />
                        {site.nom}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            
            {/* Period Selection */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#5BA4CF]" />
                Période
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700 dark:text-zinc-300 mb-2 block">Date de début</Label>
                  <div className="relative">
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="h-12 font-medium text-white pr-10"
                      style={{
                        background: isDark ? '#1f2937' : '#ffffff',
                        borderColor: '#5BA4CF',
                        borderWidth: '2px',
                        color: isDark ? '#ffffff' : '#111827'
                      }}
                    />
                    <Calendar 
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none"
                      style={{ color: isDark ? '#ffffff' : '#374151' }}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700 dark:text-zinc-300 mb-2 block">Date de fin</Label>
                  <div className="relative">
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="h-12 font-medium pr-10"
                      style={{
                        background: isDark ? '#1f2937' : '#ffffff',
                        borderColor: '#5BA4CF',
                        borderWidth: '2px',
                        color: isDark ? '#ffffff' : '#111827'
                      }}
                    />
                    <Calendar 
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none"
                      style={{ color: isDark ? '#ffffff' : '#374151' }}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Type Selection */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#5BA4CF]" />
                Type de créneaux
              </h3>
              
              <div className="flex gap-4">
                <button
                  onClick={() => setIncludeOuverture(!includeOuverture)}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    includeOuverture 
                      ? 'border-[#5BA4CF] bg-[#5BA4CF]/10' 
                      : 'border-gray-200 dark:border-zinc-700'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    {includeOuverture && <Check className="h-5 w-5 text-[#5BA4CF]" />}
                    <span className="text-2xl">🌅</span>
                    <span className="font-medium text-gray-900 dark:text-white">Ouvertures</span>
                  </div>
                </button>
                
                <button
                  onClick={() => setIncludeFermeture(!includeFermeture)}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    includeFermeture 
                      ? 'border-[#5BA4CF] bg-[#5BA4CF]/10' 
                      : 'border-gray-200 dark:border-zinc-700'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    {includeFermeture && <Check className="h-5 w-5 text-[#5BA4CF]" />}
                    <span className="text-2xl">🌙</span>
                    <span className="font-medium text-gray-900 dark:text-white">Fermetures</span>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Delay/Advance Options */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#5BA4CF]" />
                Options de détail
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => setShowDelayMinutes(!showDelayMinutes)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    showDelayMinutes 
                      ? 'border-red-400 bg-red-50 dark:bg-red-900/20' 
                      : 'border-gray-200 dark:border-zinc-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      showDelayMinutes ? 'border-red-500 bg-red-500' : 'border-gray-300 dark:border-zinc-600'
                    }`}>
                      {showDelayMinutes && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Afficher les minutes de retard</p>
                      <p className="text-sm text-gray-500 dark:text-zinc-400">Ex: Retard +12 min</p>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => setShowAdvanceMinutes(!showAdvanceMinutes)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    showAdvanceMinutes 
                      ? 'border-green-400 bg-green-50 dark:bg-green-900/20' 
                      : 'border-gray-200 dark:border-zinc-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      showAdvanceMinutes ? 'border-green-500 bg-green-500' : 'border-gray-300 dark:border-zinc-600'
                    }`}>
                      {showAdvanceMinutes && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Afficher les minutes d&#39;avance</p>
                      <p className="text-sm text-gray-500 dark:text-zinc-400">Ex: Avance -5 min</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Schedule Selection */}
            {selectedSiteId && (
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#5BA4CF]" />
                    Horaires à inclure
                  </h3>
                  {filteredSchedule.length > 0 && (
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={selectAllSchedules}
                        className="bg-[#5BA4CF] hover:bg-[#4a93be] text-white border-0"
                      >
                        Tout
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={deselectAllSchedules}
                        className="bg-gray-700 hover:bg-gray-600 dark:bg-zinc-600 dark:hover:bg-zinc-500 text-white border-0"
                      >
                        Aucun
                      </Button>
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-gray-500 dark:text-zinc-400 mb-4">
                  Sélectionnez les créneaux horaires du site à inclure dans le compte rendu
                </p>
                
                {filteredSchedule.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {filteredSchedule.map((opening, idx) => {
                      const originalIdx = siteSchedule.indexOf(opening);
                      const isSelected = selectedSchedules.includes(originalIdx);
                      
                      return (
                        <button
                          key={idx}
                          onClick={() => toggleSchedule(originalIdx)}
                          className={`p-3 rounded-lg border transition-all text-left ${
                            isSelected 
                              ? 'border-[#5BA4CF] bg-[#5BA4CF]/10' 
                              : 'border-gray-200 dark:border-zinc-700 opacity-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{opening.type === 'fermeture' ? '🌙' : '🌅'}</span>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                              {DAYS_FR[opening.day]} {opening.time}
                            </p>
                            {opening.description && (
                              <p className="text-xs text-gray-500 dark:text-zinc-400 truncate">
                                {opening.description}
                              </p>
                            )}
                          </div>
                          {isSelected && <Check className="h-4 w-4 text-[#5BA4CF] ml-auto" />}
                        </div>
                      </button>
                    );
                  })}
                  </div>
                ) : (
                  <div className="p-6 rounded-lg text-center" style={{ background: isDark ? '#374151' : '#f3f4f6' }}>
                    <Clock className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500 dark:text-zinc-400">
                      Aucun horaire configuré pour ce site
                    </p>
                    <p className="text-sm text-gray-400 dark:text-zinc-500 mt-1">
                      Configurez les horaires dans la section "Secteurs"
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Custom Message */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#5BA4CF]" />
                Message personnalisé (optionnel)
              </h3>
              
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Ajoutez un message qui apparaîtra sur le document..."
                className="w-full h-24 p-3 rounded-lg border resize-none text-gray-900 dark:text-white"
                style={{
                  background: isDark ? '#374151' : '#f8f9fa',
                  borderColor: isDark ? '#4b5563' : '#e5e7eb'
                }}
              />
            </div>
          </div>
          
          {/* Right column - Preview & Export */}
          <div className="space-y-6">
            <div className="glass-card rounded-xl p-6 sticky top-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Download className="h-5 w-5 text-[#5BA4CF]" />
                Exporter le compte rendu
              </h3>
              
              {/* Summary */}
              <div className="p-4 rounded-lg mb-4" style={{ background: isDark ? '#374151' : '#f3f4f6' }}>
                <p className="text-sm text-gray-600 dark:text-zinc-400 mb-2">Résumé</p>
                <ul className="space-y-1 text-sm">
                  <li className="flex items-center gap-2 text-gray-900 dark:text-white">
                    <MapPin className="h-4 w-4 text-[#5BA4CF]" />
                    {selectedSite?.nom || 'Aucun site sélectionné'}
                  </li>
                  <li className="flex items-center gap-2 text-gray-900 dark:text-white">
                    <Calendar className="h-4 w-4 text-[#5BA4CF]" />
                    {startDate && endDate 
                      ? `${new Date(startDate).toLocaleDateString('fr-FR')} - ${new Date(endDate).toLocaleDateString('fr-FR')}`
                      : 'Période non définie'
                    }
                  </li>
                  <li className="flex items-center gap-2 text-gray-900 dark:text-white">
                    <Clock className="h-4 w-4 text-[#5BA4CF]" />
                    {selectedSchedules.length} créneau(x) sélectionné(s)
                  </li>
                </ul>
              </div>
              
              {/* Export Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={generatePDF}
                  disabled={!selectedSiteId || !startDate || !endDate || generatingReport}
                  className="w-full bg-red-500 hover:bg-red-600 text-white"
                >
                  {generatingReport ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">📄</span>
                  )}
                  Exporter en PDF
                </Button>
                
                <Button
                  onClick={generateCSV}
                  disabled={!selectedSiteId || !startDate || !endDate || generatingReport}
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                >
                  {generatingReport ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">📊</span>
                  )}
                  Exporter en CSV
                </Button>
                
                <Button
                  onClick={generateExcel}
                  disabled={!selectedSiteId || !startDate || !endDate || generatingReport}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {generatingReport ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <span className="mr-2">📗</span>
                  )}
                  Exporter en Excel
                </Button>
              </div>
              
              {!selectedSiteId && (
                <p className="text-center text-sm text-gray-500 dark:text-zinc-400 mt-4">
                  Sélectionnez un site pour exporter
                </p>
              )}
            </div>
            
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
